<template>
  <component
    :is="allowDrop ? Drop : 'tr'"
    v-bind="allowDrop ? { tag: 'tr' } : {}"
    @drop="emit('drop', $event)"
  >
    <th
      v-for="(col, index) in headerColumns"
      :key="col.key"
      :class="{ 'no-print': col.isActions, 'sub-grid-th': subGrid }"
      :data-qa="col.key + '_heading'"
      :style="col.style"
      @click="onHeaderClick(col, $event)"
    >
      <div
        v-if="index === 0"
        class="bm-grid-row-controls"
        :style="{ marginLeft: firstColumnIndentPx + 'px' }"
      >
        <bm-grid-row-selector
          v-if="allowSelect"
          :checked="headerSelectChecked"
          :disabled="headerSelectDisabled"
          :is-header="true"
          @change="emit('toggle-select-all')"
        />
        <div class="d-flex align-center header-cell" :class="alignClass(col)">
          <template v-if="col.column.align === 'end'">
            <bm-icon
              v-if="col.showSortIcon"
              icon="KeyboardArrowDown"
              class="expand-icon"
              :class="sortIconClass(col)"
            />
            <span v-if="col.showSortIndex">{{ col.sortIndex + 1 }}</span>
            <span>{{ col.column.headingText }}</span>
          </template>
          <template v-else>
            <span>{{ col.column.headingText }}</span>
            <bm-icon
              v-if="col.showSortIcon"
              icon="KeyboardArrowDown"
              class="expand-icon"
              :class="sortIconClass(col)"
            />
            <span v-if="col.showSortIndex">{{ col.sortIndex + 1 }}</span>
          </template>
        </div>
      </div>
      <div
        v-else
        class="d-flex align-center header-cell"
        :class="alignClass(col)"
      >
        <template v-if="col.column.align === 'end'">
          <bm-icon
            v-if="col.showSortIcon"
            icon="KeyboardArrowDown"
            class="expand-icon"
            :class="sortIconClass(col)"
          />
          <span v-if="col.showSortIndex">{{ col.sortIndex + 1 }}</span>
          <span>{{ col.column.headingText }}</span>
        </template>
        <template v-else>
          <span>{{ col.column.headingText }}</span>
          <bm-icon
            v-if="col.showSortIcon"
            icon="KeyboardArrowDown"
            class="expand-icon"
            :class="sortIconClass(col)"
          />
          <span v-if="col.showSortIndex">{{ col.sortIndex + 1 }}</span>
        </template>
      </div>
    </th>
  </component>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { BmGridColumnConfigurations } from "@/components/controls/grid/BmGridData";
import type { BmGridColumnSortState } from "@/components/controls/grid/state/BmGridState";
import BmGridRowSelector from "@/components/controls/grid/BmGridRowSelector.vue";
import type { DnDEvent } from "vue-easy-dnd";
import { Drop } from "vue-easy-dnd";

interface BmGridTableHeadProps {
  visibleColumns: BmGridColumnConfigurations;
  sortBy: readonly BmGridColumnSortState[];
  allowSort: boolean;
  allowSelect: boolean;
  allowDrop: boolean;
  firstColumnIndentPx: number;
  headerSelectChecked: boolean;
  headerSelectDisabled: boolean;
  subGrid?: boolean;
}

const props = withDefaults(defineProps<BmGridTableHeadProps>(), {
  subGrid: false,
});

interface BmGridTableHeadEvents {
  (event: "toggle-select-all"): void;
  (
    event: "sort-column",
    payload: { columnKey: string; shiftKey: boolean }
  ): void;
  (event: "drop", eventPayload: DnDEvent): void;
}

const emit = defineEmits<BmGridTableHeadEvents>();

type HeaderColumn = {
  key: string;
  column: BmGridColumnConfigurations[string];
  sortIndex: number;
  showSortIcon: boolean;
  showSortIndex: boolean;
  sortAscending: boolean;
  isActions: boolean;
  style: { width?: string; minWidth?: string };
};

const headerColumns = computed((): HeaderColumn[] =>
  Object.entries(props.visibleColumns).map(([key, column]) => {
    const sortIndex = props.sortBy.findIndex(
      (sortBy) => sortBy.columnKey === key
    );
    return {
      key,
      column,
      sortIndex,
      showSortIcon:
        (props.allowSort && column.sortableAndFilterable) || sortIndex !== -1,
      showSortIndex: props.sortBy.length > 1 && sortIndex >= 0,
      sortAscending: !(props.sortBy[sortIndex]?.descending ?? false),
      isActions: !!column.rendererMetadata.actions,
      style: column.shrink
        ? { width: "1%", minWidth: "1%" }
        : ({} as HeaderColumn["style"]),
    };
  })
);

function alignClass(col: HeaderColumn) {
  return {
    "justify-center": col.column.align === "center",
    "justify-end": col.column.align === "end",
  };
}

function sortIconClass(col: HeaderColumn) {
  return {
    "expand-icon-active": col.sortAscending,
    "hover-reveal-icon": col.sortIndex === -1,
  };
}

function onHeaderClick(col: HeaderColumn, e: MouseEvent) {
  if (props.allowSort && col.column.sortableAndFilterable) {
    emit("sort-column", { columnKey: col.key, shiftKey: e.shiftKey });
  }
}
</script>

<style scoped lang="sass">
@import "src/sass/typography"
@import "src/sass/colours"

// Mirror BmGrid thead th styles — parent scoped rules do not reach child <th>.
th
  @include bm-body
  color: $bm-color-text01
  text-align: left
  border-bottom: 3px solid #D1CFD7
  padding-bottom: 3px
  vertical-align: bottom
  position: sticky
  position: -webkit-sticky
  top: 0
  background-color: $bm-color-background01
  z-index: 2
  cursor: pointer
  user-select: none
  white-space: nowrap

th.sub-grid-th
  z-index: 1

th:first-child
  padding-left: 0

.bm-grid-row-controls
  display: flex
  align-items: flex-end

.header-cell
  padding: 0 8px

.header-cell .hover-reveal-icon
  opacity: 0

.header-cell:hover .hover-reveal-icon
  opacity: 0.3

.expand-icon
  user-select: none
  cursor: pointer
  color: inherit

.expand-icon-active
  transform: rotate(-180deg)

// vue-easy-dnd .drop-in indicator (header drop target)
.drop-in th, :deep(.drop-in) th
  border-bottom: 3px solid
  border-top: inherit
  transition: 0.1s

.drop-in.drop-above th, :deep(.drop-in.drop-above) th
  border-top: 3px solid
  border-bottom: inherit
  transition: 0.1s

@media print
  .expand-icon
    display: none
</style>
